/** Two runs side by side — what was sent and what came back — with changed rows highlighted. */

import type { LnTestResponse, OfacTestResponse } from '@/services/verificationTestApi';

import { INDEX_LABEL, alertsOf } from './LnResultPanel';

export interface ComparableRun {
  id: number;
  at: Date;
  ln?: LnTestResponse;
  ofac?: OfacTestResponse;
}

type Getter = (r: ComparableRun) => string;

const ln = (f: (x: LnTestResponse) => string): Getter => (r) => (r.ln ? f(r.ln) : 'not run');

const ROWS: Array<[string, Getter]> = [
  ['When', (r) => r.at.toLocaleTimeString()],
  ['Environment', ln((x) => (x.environment === 'sandbox' ? 'LexisNexis test environment' : 'Production'))],
  ['Name', ln((x) => [x.subject?.firstName, x.subject?.middleInitial, x.subject?.lastName].filter(Boolean).join(' '))],
  ['Date of birth', ln((x) => x.subject?.dob ?? '')],
  ['SSN (last 4)', ln((x) => x.subject?.taxIdLast4 ?? '')],
  ['Address', ln((x) => [x.subject?.street, x.subject?.city, x.subject?.state, x.subject?.zip].filter(Boolean).join(', '))],
  ['Phone', ln((x) => x.subject?.phone ?? '')],
  ['E-mail', ln((x) => x.subject?.email ?? '')],
  ['Changed from Core Director', ln((x) => (x.editedFields ?? []).join(', ') || '—')],
  ['FraudPoint score', ln((x) => (x.success ? String(x.fraudPoint?.score ?? '—') : `no result: ${x.error ?? ''}`))],
  ['Band', ln((x) => x.fraudPoint?.band ?? '—')],
  ['Cut-offs used', ln((x) => (x.fraudPoint ? `${x.fraudPoint.lowFloor} / ${x.fraudPoint.moderateFloor}` : '—'))],
  ['Effect on account opening', ln((x) => x.transparency?.verdict.commitText ?? '—')],
  ...Object.keys(INDEX_LABEL).map((k): [string, Getter] => [INDEX_LABEL[k] ?? k, ln((x) => String(x.fraudPoint?.indices?.[k] ?? '—'))]),
  ['Name / address / phone', ln((x) => String(x.instantId?.nameAddressPhone ?? '—'))],
  ['Name / address / SSN', ln((x) => String(x.instantId?.nameAddressSsn ?? '—'))],
  ['Date of birth match', ln((x) => String(x.instantId?.dobMatchLevel ?? '—'))],
  ['Emailage', ln((x) => (x.emailage?.called ? `${x.emailage.score ?? '—'} ${x.emailage.advice ?? ''}` : 'not checked'))],
  ['Alerts', ln((x) => Array.from(new Set(alertsOf(x).map((a) => a.text))).join(' · ') || 'none')],
  ['OFAC result', (r) => (r.ofac ? r.ofac.transparency?.verdict.officerPill ?? r.ofac.status ?? '—' : 'not run')],
  ['OFAC possible matches', (r) => (r.ofac ? String(r.ofac.matches?.length ?? 0) : 'not run')],
];

export function RunCompare({ a, b }: { a: ComparableRun; b: ComparableRun }): JSX.Element {
  const [older, newer] = a.at <= b.at ? [a, b] : [b, a];
  let changed = 0;
  const rows = ROWS.map(([label, get]) => {
    const va = get(older);
    const vb = get(newer);
    const diff = label !== 'When' && va !== vb;
    if (diff) changed++;
    return { label, va, vb, diff };
  });
  return (
    <div>
      <p className="vc-hint" style={{ marginBottom: 6 }}>{changed} row{changed === 1 ? '' : 's'} changed. Changed rows are highlighted.</p>
      <div className="vc-table-wrap">
        <table className="vc-table">
          <thead><tr><th /><th>Run #{older.id}</th><th>Run #{newer.id}</th></tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.label} className={r.diff ? 'is-changed' : undefined}>
                <th scope="row">{r.label}</th>
                <td>{r.va}</td>
                <td>{r.vb}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
